import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Share2, Download, Copy, Send } from "lucide-react";
import { toast } from "sonner";

const W = 1080;
const H = 1350;

function drawCard(canvas, brief) {
  if (!canvas || !brief) return;
  const ctx = canvas.getContext("2d");
  canvas.width = W;
  canvas.height = H;

  const s = brief.schumann || {};
  const c = brief.cosmic || {};
  const b = brief.brief || {};

  const bg = "#0a1128";
  const surface = "rgba(19, 27, 54, 0.75)";
  const gold = "#d4a24c";
  const parchment = "#f5e6c8";
  const parchmentSoft = "#e9dcc0";
  const rose = "#e5a87c";
  const muted = "#b8afa1";

  // Background
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Starfield
  const rng = mulberry32(42);
  ctx.fillStyle = parchment;
  for (let i = 0; i < 120; i++) {
    const x = rng() * W;
    const y = rng() * H;
    const r = rng() * 1.6 + 0.4;
    ctx.globalAlpha = 0.15 + rng() * 0.45;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Header
  ctx.fillStyle = gold;
  ctx.font = "500 22px 'Instrument Sans', sans-serif";
  ctx.textBaseline = "top";
  const dateStr = new Date().toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
  ctx.fillText(dateStr.toUpperCase(), 80, 90);

  ctx.fillStyle = parchment;
  ctx.font = "italic 34px 'Instrument Serif', serif";
  ctx.fillText("Earth · Pulse", 80, 128);

  ctx.fillStyle = muted;
  ctx.font = "500 18px 'Instrument Sans', sans-serif";
  ctx.textAlign = "right";
  ctx.fillText("DAILY COSMIC GUIDANCE", W - 80, 128);
  ctx.textAlign = "left";

  // Divider
  ctx.strokeStyle = "rgba(212, 162, 76, 0.3)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(80, 200);
  ctx.lineTo(W - 80, 200);
  ctx.stroke();

  // Sensitivity chip
  const sens = b.sensitivity_level || "moderate";
  const sensColor = sens === "high" ? "#e2725b" : sens === "moderate" ? rose : gold;
  ctx.strokeStyle = sensColor;
  ctx.fillStyle = sensColor;
  ctx.font = "500 18px 'Instrument Sans', sans-serif";
  const chipText = `SENSITIVITY  ${sens.toUpperCase()}`;
  const chipW = ctx.measureText(chipText).width + 32;
  roundRect(ctx, 80, 230, chipW, 40, 20);
  ctx.stroke();
  ctx.fillText(chipText, 96, 240);

  // Headline — serif display
  ctx.fillStyle = parchment;
  ctx.font = "600 60px 'Instrument Serif', 'Fraunces', serif";
  const headline = (b.headline || "Today's Earth Pulse briefing.").trim();
  const hLines = wrapText(ctx, headline, W - 160).slice(0, 4);
  let hy = 310;
  hLines.forEach((line) => {
    ctx.fillText(line, 80, hy);
    hy += 72;
  });

  // Big readings — three columns
  const startY = Math.max(hy + 40, 620);
  const cols = [
    { label: "SCHUMANN", value: `${(s.base_freq_hz ?? 7.83).toFixed(2)}`, unit: "Hz", color: gold, note: (s.status || "").toLowerCase() },
    { label: "KP INDEX", value: `${(c.kp_index ?? 0).toFixed(1)}`, unit: c.kp_status || "", color: rose, note: "" },
    { label: "X-RAY", value: c.xray_flux_class || "A0.0", unit: c.geomag_status || "", color: parchmentSoft, note: "" },
  ];
  const colW = (W - 160) / 3;
  cols.forEach((col, i) => {
    const x = 80 + i * colW;
    ctx.fillStyle = muted;
    ctx.font = "500 18px 'Instrument Sans', sans-serif";
    ctx.fillText(col.label, x, startY);
    ctx.fillStyle = col.color;
    ctx.font = "500 92px 'Instrument Serif', serif";
    ctx.fillText(col.value, x, startY + 28);
    ctx.fillStyle = muted;
    ctx.font = "italic 20px 'Instrument Serif', serif";
    ctx.fillText(col.unit, x, startY + 138);
  });

  // Divider
  const reportY = startY + 210;
  ctx.strokeStyle = "rgba(212, 162, 76, 0.3)";
  ctx.beginPath();
  ctx.moveTo(80, reportY);
  ctx.lineTo(W - 80, reportY);
  ctx.stroke();

  // Earth report
  ctx.fillStyle = gold;
  ctx.font = "500 18px 'Instrument Sans', sans-serif";
  ctx.fillText("EARTH REPORT", 80, reportY + 24);
  ctx.fillStyle = parchmentSoft;
  ctx.font = "400 26px 'Instrument Sans', sans-serif";
  const earthLines = wrapText(ctx, b.earth_report || "", W - 160).slice(0, 4);
  let ey = reportY + 62;
  earthLines.forEach((line) => {
    ctx.fillText(line, 80, ey);
    ey += 36;
  });

  // Footer
  const footerY = H - 90;
  ctx.strokeStyle = "rgba(212, 162, 76, 0.3)";
  ctx.beginPath();
  ctx.moveTo(80, footerY);
  ctx.lineTo(W - 80, footerY);
  ctx.stroke();
  ctx.fillStyle = muted;
  ctx.font = "500 16px 'Instrument Sans', sans-serif";
  ctx.fillText("SOURCES · NOAA SWPC · RESONANCEONE · EARTHPULSE NARRATIVE ENGINE", 80, footerY + 26);
  ctx.textAlign = "right";
  ctx.fillStyle = gold;
  ctx.font = "italic 22px 'Instrument Serif', serif";
  ctx.fillText("Earth · Pulse", W - 80, footerY + 22);
  ctx.textAlign = "left";
  suppressUnused(surface);
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
function mulberry32(a) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function suppressUnused(_) {}

function wrapText(ctx, text, maxWidth) {
  const words = text.split(/\s+/);
  const lines = [];
  let current = "";
  for (const w of words) {
    const test = current ? `${current} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = w;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function buildCaption(brief) {
  const b = brief?.brief || {};
  const s = brief?.schumann || {};
  const c = brief?.cosmic || {};
  const tags = (b.tags || []).map((t) => `#${t.replace(/\s+/g, "")}`).join(" ");
  return [
    `Earth · Pulse — ${new Date().toLocaleDateString()}`,
    "",
    b.headline || "Today's briefing.",
    "",
    `Schumann ${s.base_freq_hz?.toFixed(2)} Hz · Kp ${c.kp_index?.toFixed(1)} · X-ray ${c.xray_flux_class}`,
    `Sensitivity: ${b.sensitivity_level || "moderate"}`,
    "",
    tags,
    "#schumannresonance #spaceweather #earthpulse",
  ].join("\n");
}

export default function ShareCard({ brief }) {
  const canvasRef = useRef(null);
  const [open, setOpen] = useState(false);
  const canShare = typeof navigator !== "undefined" && !!navigator.canShare;

  useEffect(() => {
    if (open) {
      // Draw after modal opens so fonts are loaded
      const t = setTimeout(() => drawCard(canvasRef.current, brief), 80);
      return () => clearTimeout(t);
    }
  }, [open, brief]);

  const download = () => {
    const url = canvasRef.current.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `earth-pulse-${new Date().toISOString().slice(0, 10)}.png`;
    a.click();
    toast.success("Card downloaded");
  };

  const shareNative = async () => {
    try {
      const blob = await new Promise((res) => canvasRef.current.toBlob(res, "image/png"));
      const file = new File([blob], "earth-pulse.png", { type: "image/png" });
      const data = { files: [file], title: "Earth Pulse", text: buildCaption(brief) };
      if (navigator.canShare && navigator.canShare(data)) {
        await navigator.share(data);
        toast.success("Shared");
      } else {
        toast("Native share isn't supported in this browser", { description: "Try Download or Copy caption." });
      }
    } catch (e) {
      if (e.name !== "AbortError") toast.error("Share failed");
    }
  };

  const copyCaption = async () => {
    await navigator.clipboard.writeText(buildCaption(brief));
    toast.success("Caption copied");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="share-card-open"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[color:var(--gold)]/40 hover:border-[color:var(--gold)] text-[color:var(--parchment)] font-sans-body text-sm"
          style={{ transition: "border-color 200ms ease, background-color 200ms ease" }}
        >
          <Share2 size={14} /> Share today
        </button>
      </DialogTrigger>
      <DialogContent
        className="max-w-[580px] bg-[color:var(--bg-surface)] border-[color:var(--gold)]/25 rounded-xl p-6"
        data-testid="share-card-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display tracking-tight text-white text-2xl">
            Cosmic weather card
          </DialogTitle>
        </DialogHeader>
        <div className="mt-3">
          <div className="rounded-xl border border-[color:var(--gold)]/20 overflow-hidden">
            <canvas
              ref={canvasRef}
              className="w-full h-auto block"
              style={{ aspectRatio: `${W} / ${H}` }}
              data-testid="share-card-canvas"
            />
          </div>
          <div className="grid grid-cols-3 gap-3 mt-5">
            <Button
              onClick={download}
              className="rounded-full bg-[color:var(--gold)] text-[color:var(--bg-base)] hover:bg-[color:var(--gold-soft)] font-sans-body text-sm"
              data-testid="share-download"
            >
              <Download size={14} className="mr-1" /> PNG
            </Button>
            <Button
              onClick={shareNative}
              disabled={!canShare}
              variant="outline"
              className="rounded-full border-[color:var(--gold)]/40 bg-transparent hover:bg-[color:var(--gold)]/10 text-[color:var(--parchment)] font-sans-body text-sm disabled:opacity-40"
              data-testid="share-native"
            >
              <Send size={14} className="mr-1" /> Share
            </Button>
            <Button
              onClick={copyCaption}
              variant="outline"
              className="rounded-full border-[color:var(--gold)]/40 bg-transparent hover:bg-[color:var(--gold)]/10 text-[color:var(--parchment)] font-sans-body text-sm"
              data-testid="share-copy-caption"
            >
              <Copy size={14} className="mr-1" /> Caption
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
